import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Wrench, ClipboardList, Truck, Shield, Clock, Award, Leaf, Recycle, Heart, Home, Building2, UtensilsCrossed, Phone, Mail } from "lucide-react";
import Layout from "@/components/Layout";
import Reviews from "@/components/Reviews";
import CustomerServiceCTA from "@/components/CustomerServiceCTA";
import { useLive } from "@/lib/serviceInfo";

const HERO = "/assets/hero-workshop-repair.png";
const DETAIL = "/assets/service-machine-detail.png";

export default function Index() {
  const live = useLive();
  return (
    <Layout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Bergen Kaffemaskin- og Teknikk Service" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#3D2314]/90 via-[#3D2314]/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight font-display">
              Reparasjon og service av kaffemaskiner i Bergen
            </h1>
            <p className="text-lg text-white/90 mb-8">
              Ditt lokale serviceverksted i Bergen – med transparent prising og ingen overraskelser.
              Vi hjelper deg med alt fra enkle hjemmeapparater til avansert bedriftsutstyr.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-[#D4A574] hover:bg-[#D4A574]/90 text-[#3D2314] font-semibold text-base">
                <Link to="/innlevering" data-testid="hero-innlevering">Lever inn ditt utstyr</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 hover:text-white">
                <Link to="/tjenester" data-testid="hero-priser">Se priser</Link>
              </Button>
            </div>
            <div className="mt-8 rounded-xl border border-[#D4A574]/60 bg-[#3D2314]/80 backdrop-blur-sm p-5 text-white space-y-3" data-testid="home-intake-notice">
              <p className="text-sm md:text-base"><strong className="text-[#D4A574]">Lever inn kaffemaskinen komplett.</strong> Ta med alle delene som hører til maskinen, for eksempel filterholder, filter, vanntank, dryppbrett og bønnebeholder. Manglende deler kan forsinke feilsøkingen og medføre ekstra kostnader.</p>
              <p className="text-sm md:text-base"><strong className="text-[#D4A574]">Registrer innleveringen før du kommer.</strong> Fyll ut <Link to="/innlevering" className="underline text-[#D4A574]" data-testid="home-notice-innlevering">innleveringsskjemaet</Link> på nettsiden og beskriv problemet så godt du kan. Du får et ordrenummer, og verkstedet får nødvendig informasjon før maskinen leveres inn. Det gjør mottaket enklere og hjelper mekanikeren med å planlegge feilsøkingen.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-[#3D2314] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 mb-4 text-center"><p className="text-white/80 text-sm font-medium">Vi reparerer blant annet:</p></div>
        <div className="relative">
          <div className="flex animate-marquee whitespace-nowrap w-max">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center gap-10 mx-10">
                {["Jura", "Siemens", "Bosch", "Delonghi", "Miele", "Philips", "Saeco", "Melitta", "Nivona", "Sage", "Krups", "La Cimbali M100"].map((b) => (
                  <span key={`${b}-${i}`} className="text-[#D4A574] font-bold text-xl whitespace-nowrap">{b}</span>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 mt-4 text-center"><p className="text-white/60 text-sm">Vi reparerer også: Symaskiner og andre maskiner – også storkjøkkenmaskiner for hoteller, kafeer, restauranter, båter, bensinstasjoner osv.</p></div>
      </section>

      <section className="py-10 md:py-14 bg-[#FFF8F0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#3D2314]/80 text-base md:text-lg leading-relaxed">
            Vi jobber raskt og effektivt, men noen ganger er det mye på gang – og da kan en reparasjon ta litt lenger tid enn planlagt.
            Av og til må vi også finne en ekstra løsning for å gi deg det beste resultatet.
            Kvalitet tar tid, og vi leverer ikke fra oss noe før det er ordentlig gjort.
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#3D2314] mb-2 font-display">Vi utfører service og reparasjon på blant annet:</h2>
          </div>
          <div className="mb-8">
            <h3 className="text-center text-sm font-semibold uppercase tracking-wider text-[#D4A574] mb-4">Hjemmemaskiner</h3>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              {["Jura", "Siemens", "Bosch", "DeLonghi", "Melitta", "Philips", "Saeco", "Krups", "Nivona", "Miele", "Sage / Breville"].map((b) => (
                <span key={b} className="px-4 py-2 rounded-full bg-[#FFF8F0] border border-[#D4A574]/30 text-[#3D2314] font-medium text-sm md:text-base whitespace-nowrap">{b}</span>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-center text-sm font-semibold uppercase tracking-wider text-[#D4A574] mb-4">Profesjonelle kaffe- og espressomaskiner</h3>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              {["La Marzocco", "Rocket", "Rancilio", "Nuova Simonelli", "Franke", "WMF", "Eversys", "La Cimbali M100"].map((b) => (
                <span key={b} className="px-4 py-2 rounded-full bg-[#3D2314]/5 border border-[#3D2314]/20 text-[#3D2314] font-medium text-sm md:text-base whitespace-nowrap">{b}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#3D2314] mb-4 font-display">Slik fungerer det</h2>
            <p className="text-[#1A1A1A]/70 max-w-2xl mx-auto">Tre enkle steg fra innlevering til ferdig reparert utstyr</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: ClipboardList, step: "1", title: "Fyll ut skjema", description: "Beskriv problemet og velg servicepakke via vårt digitale innleveringsskjema." },
              { icon: Truck, step: "2", title: "Lever inn utstyret", description: "Bring utstyret til verkstedet i Birkeveien 2, Bergen – eller avtal henting." },
              { icon: Wrench, step: "3", title: "Vi fikser det", description: "Våre teknikere diagnostiserer og reparerer. Du får beskjed når det er klart." },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4A574]/15 mb-4">
                  <item.icon className="h-8 w-8 text-[#3D2314]" />
                </div>
                <h3 className="text-xl font-semibold text-[#3D2314] mb-2">{item.title}</h3>
                <p className="text-[#1A1A1A]/70">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#FFF8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#3D2314] mb-4 font-display">Våre servicepakker</h2>
            <p className="text-[#1A1A1A]/70 max-w-2xl mx-auto">Velg pakken som passer ditt behov. Pakke A har fast pris; Pakke B og C har veiledende «fra-priser» – endelig pris bekreftes etter diagnostikk, og deler kommer i tillegg.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { id: "A", name: "Standard diagnostikk", price: live.priceA, suffix: "inkl. mva · inntil 2 arbeidstimer", popular: false, items: ["Feilsøking og diagnostikk", "Tilstandsrapport", "Prisoverslag på reparasjon"] },
              { id: "B", name: "Forebyggende service", price: live.priceB, suffix: "inkl. mva", popular: true, items: ["Alt i Pakke A", "Full rengjøring og avkalking", "Bytte av slitasjedeler", "Funksjonstest etter service"] },
              { id: "C", name: "Utvidet diagnostikk", price: live.priceC, suffix: "per dag inkl. mva", popular: false, items: ["Alt i Pakke B", "Dyptgående feilsøking", "Komponentnivå-reparasjon", "Prioritert behandling"] },
            ].map((pkg) => (
              <Card key={pkg.id} className={`relative ${pkg.popular ? "border-[#D4A574] shadow-lg" : "border-[#D4A574]/30 hover:shadow-lg transition-shadow"}`}>
                {pkg.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A574] text-[#3D2314] text-xs font-bold px-3 py-1 rounded-full">Mest populær</div>}
                <CardHeader className="text-center pb-2">
                  <div className="text-sm font-medium text-[#D4A574] mb-1">Pakke {pkg.id}</div>
                  <CardTitle className="text-xl text-[#3D2314]">{pkg.name}</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="text-3xl font-bold text-[#3D2314] mb-1" data-testid={`home-price-${pkg.id}`}>{pkg.id === "A" ? "" : "fra "}{pkg.price}</div>
                  <div className="text-sm text-[#1A1A1A]/60 mb-4">{pkg.suffix}</div>
                  <ul className="text-sm text-left space-y-2 mb-6">
                    {pkg.items.map((it) => (
                      <li key={it} className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" /><span>{it}</span></li>
                    ))}
                  </ul>
                  <Button asChild className={`w-full ${pkg.popular ? "bg-[#D4A574] hover:bg-[#D4A574]/90 text-[#3D2314] font-semibold" : "bg-[#3D2314] hover:bg-[#3D2314]/90 text-white"}`}>
                    <Link to="/innlevering">Velg pakke {pkg.id}</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-xs text-[#1A1A1A]/60 mt-6 max-w-2xl mx-auto">Pakke A har fast pris inkl. mva for inntil 2 arbeidstimer. Prisene for Pakke B og C er veiledende basispriser (fra-priser) inkl. mva. Deler, materialer og transport kommer i tillegg og avtales før arbeidet fortsetter. Diagnostikkgebyret trekkes fra ved en større reparasjon. Se <Link to="/vilkar" className="underline text-[#D4A574]">vilkår</Link> og <Link to="/faq" className="underline text-[#D4A574]">ofte stilte spørsmål</Link>.</p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#3D2314] mb-6 font-display">Hvorfor velge oss?</h2>
              <div className="space-y-4">
                {[
                  { icon: Shield, title: "Transparent prising", desc: "Tydelige fra-priser og prisoverslag etter diagnostikk – du godkjenner før arbeidet fortsetter." },
                  { icon: Award, title: "Erfarne teknikere", desc: "Våre teknikere har mange års erfaring med et bredt spekter av utstyr." },
                  { icon: Clock, title: "Rask behandling", desc: "Du får et realistisk tidsestimat, og vi holder deg oppdatert underveis." },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="shrink-0 w-10 h-10 rounded-lg bg-[#D4A574]/15 flex items-center justify-center"><item.icon className="h-5 w-5 text-[#3D2314]" /></div>
                    <div><h3 className="font-semibold text-[#3D2314]">{item.title}</h3><p className="text-sm text-[#1A1A1A]/70">{item.desc}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img src={DETAIL} alt="Service av utstyr" className="w-full h-80 object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-[#3D2314]" data-testid="home-service-statement">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg md:text-xl font-medium text-white font-display">Reparasjon, service og vedlikehold av kaffemaskiner, symaskiner og storkjøkkenutstyr i Bergen.</p>
        </div>
      </section>

      <Reviews />

      <CustomerServiceCTA />

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#3D2314] mb-4 font-display">Miljø og bærekraft</h2>
            <p className="text-[#1A1A1A]/70 max-w-2xl mx-auto">Vi tror på å reparere fremfor å kaste. Hver reparasjon er et bidrag til et bedre miljø.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Recycle, title: "Reduser e-avfall", description: "Ved å reparere istedenfor å kaste, bidrar du til å redusere mengden elektronisk avfall som havner på søppelfyllinger." },
              { icon: Heart, title: "Forleng levetiden", description: "De fleste apparater kan leve mye lenger med riktig vedlikehold og service. Vi hjelper deg å få mest mulig ut av utstyret ditt." },
              { icon: Leaf, title: "Spar penger og miljø", description: "En reparasjon koster ofte en brøkdel av nytt utstyr – og sparer ressursene som kreves for å produsere nytt." },
            ].map((item) => (
              <div key={item.title} className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 mb-4"><item.icon className="h-7 w-7 text-green-700" /></div>
                <h3 className="text-lg font-semibold text-[#3D2314] mb-2">{item.title}</h3>
                <p className="text-sm text-[#1A1A1A]/70">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 bg-[#FFF8F0]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mb-4">
            {[{ icon: Home, label: "Private hjem" }, { icon: Building2, label: "Bedrifter & kontorer" }, { icon: UtensilsCrossed, label: "Storkjøkken" }].map((s) => (
              <div key={s.label} className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#D4A574]/15 flex items-center justify-center"><s.icon className="h-4 w-4 text-[#3D2314]" /></div>
                <span className="text-sm font-medium text-[#3D2314]">{s.label}</span>
              </div>
            ))}
          </div>
          <p className="text-base text-[#1A1A1A]/80 font-medium max-w-2xl mx-auto">Vi betjener både private hjem, kontorer, bedrifter og storkjøkken med service, vedlikehold og reparasjon.</p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#3D2314] mb-4 font-display">Besøk oss</h2>
            <p className="text-[#1A1A1A]/70 max-w-2xl mx-auto">Det er enkelt å komme hit med kollektivtransport.</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg border border-[#D4A574]/20 mb-12">
            <img src="https://static.prod-images.emergentagent.com/jobs/ac17586a-8f5d-4571-afc7-c7fa9192efaa/images/ab5bd41bcbd827fee52d5d9c217feb9a9285399560f445d7858e8c754e06b4a7.jpeg" alt="Slik finner du oss – Birkeveien 2, 5093 Bergen. Inngang og parkering rett foran døren." className="w-full h-auto" data-testid="home-find-us-image" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="space-y-6">
              {[
                { icon: Clock, title: "Åpningstider", lines: [`Mandag–Fredag: ${live.hoursWeekday}`, `Lørdag–Søndag: ${live.hoursWeekend}`] },
                { icon: Truck, title: "Adresse", lines: ["Birkeveien 2, 5093 Bergen"] },
              ].map((c) => (
                <div key={c.title} className="flex items-start gap-4">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-[#D4A574]/15 flex items-center justify-center"><c.icon className="h-5 w-5 text-[#3D2314]" /></div>
                  <div><h3 className="font-semibold text-[#3D2314]">{c.title}</h3>{c.lines.map((l) => <p key={l} className="text-sm text-[#1A1A1A]/70">{l}</p>)}</div>
                </div>
              ))}
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 rounded-lg bg-[#D4A574]/15 flex items-center justify-center"><Phone className="h-5 w-5 text-[#3D2314]" /></div>
                <div><h3 className="font-semibold text-[#3D2314]">Telefon</h3><a href="tel:+4790232206" className="text-sm text-[#D4A574] hover:underline font-medium">90 23 22 06</a></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 rounded-lg bg-[#D4A574]/15 flex items-center justify-center"><Mail className="h-5 w-5 text-[#3D2314]" /></div>
                <div><h3 className="font-semibold text-[#3D2314]">E-post</h3><a href="mailto:info@bergenkaffeogteknikk.no" className="text-sm text-[#D4A574] hover:underline font-medium">info@bergenkaffeogteknikk.no</a></div>
              </div>
              <div className="bg-[#FFF8F0] rounded-xl p-5 border border-[#D4A574]/20">
                <h4 className="font-semibold text-[#3D2314] mb-2">Kollektivtransport</h4>
                <p className="text-sm text-[#1A1A1A]/70 mb-2">Nærmeste forbindelser:</p>
                <ul className="text-sm text-[#1A1A1A]/70 space-y-1">
                  {["Buss 5", "Buss 6", "Buss 20", "Buss 16E", "Bybane linje 1"].map((b) => <li key={b}>• {b}</li>)}
                </ul>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-[#FFF8F0] rounded-xl p-5 border border-[#D4A574]/20">
                <h4 className="font-semibold text-[#3D2314] mb-2">Vi deler lokaler med et systudio</h4>
                <p className="text-sm text-[#1A1A1A]/70 mb-3">Systudioet er et godt orienteringspunkt når du besøker oss. Vårt skilt er synlig i vinduet.</p>
                <p className="text-sm text-[#1A1A1A]/70 mb-3">Dersom teknikeren ikke er til stede, kan systudioet hjelpe med å ta imot eller utlevere maskiner.</p>
                <div className="bg-white rounded-lg p-4 border border-[#D4A574]/10">
                  <p className="text-xs font-semibold text-[#3D2314] uppercase tracking-wide mb-2">Viktig informasjon</p>
                  <p className="text-xs text-[#1A1A1A]/60 mb-2">Systudioet er ikke en del av Bergen Kaffemaskin- og Teknikk Service og har ikke ansvar for reparasjoner, diagnostikk, priser, garantier eller andre forhold knyttet til vår virksomhet.</p>
                  <p className="text-xs text-[#1A1A1A]/60 mb-1">De kan kun:</p>
                  <ul className="text-xs text-[#1A1A1A]/60 space-y-0.5 ml-3">
                    <li>• Ta imot maskiner for innlevering</li>
                    <li>• Utlevere ferdig reparerte maskiner</li>
                    <li>• Registrere kundens navn</li>
                    <li>• Registrere ordrenummer</li>
                  </ul>
                </div>
              </div>
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                <p className="text-sm text-amber-900 font-medium">⚠️ Kunden må oppgi eller vise ordrenummeret som ble sendt på e-post etter registrering av serviceforespørselen.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#FFF8F0]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-[#3D2314] mb-4 font-display">Klar til å få utstyret ditt fikset?</h2>
          <p className="text-[#1A1A1A]/70 mb-8">Fyll ut innleveringsskjemaet vårt, så tar vi kontakt med deg innen én virkedag.</p>
          <Button asChild size="lg" className="bg-[#D4A574] hover:bg-[#D4A574]/90 text-[#3D2314] font-semibold text-base">
            <Link to="/innlevering">Start innlevering nå</Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
}
